import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import useWilmaUsers from "../../data/useWilmaUsers";

interface WorkOrdersSearchBarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  priority: string;
  onPriorityChange: (value: string) => void;
  showAssigneeFilter: boolean;
  assignee: string;
  onAssigneeChange: (value: string) => void;
  showCompleted: boolean;
  onShowCompletedChange: (value: boolean) => void;
  statusOptions: { label: string; value: string }[];
  priorityOptions: { label: string; value: string }[];
  onNewOrderClick: () => void;
}

export default function WorkOrdersSearchBar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  priority,
  onPriorityChange,
  showAssigneeFilter,
  assignee,
  onAssigneeChange,
  showCompleted,
  onShowCompletedChange,
  statusOptions,
  priorityOptions,
  onNewOrderClick,
}: WorkOrdersSearchBarProps) {
  const { t } = useTranslation("workOrders");
  const { wilmaUsers } = useWilmaUsers();


  return (
    <div className="mt-4 bg-white shadow rounded-lg p-3">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="relative w-full lg:flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t("Search work orders...")}
            className="w-full rounded-md border border-gray-300 pl-10 pr-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>

        <select
          value={status}
          onChange={(e) => onStatusChange(e.target.value)}
          className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">{t("All Status")}</option>
          {statusOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.label)}
            </option>
          ))}
        </select>

        <select
          value={priority}
          onChange={(e) => onPriorityChange(e.target.value)}
          className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
        >
          <option value="all">{t("All Priority")}</option>
          {priorityOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {t(opt.label)}
            </option>
          ))}
        </select>

        {showAssigneeFilter && (
          <select
            value={assignee}
            onChange={(e) => onAssigneeChange(e.target.value)}
            className="w-full lg:w-44 rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="all">{t("All Assignees")}</option>
            <option value="unassigned">{t("Unassigned")}</option>
            {wilmaUsers.data?.data.map((user) => (
              <option key={user.id} value={user.id}>
                {user.fullname}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          onClick={() => onShowCompletedChange(!showCompleted)}
          className="w-full lg:w-auto rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 whitespace-nowrap"
        >
          {showCompleted ? t("Hide completed") : t("View completed")}
        </button>

        <div className="w-full lg:w-auto lg:ml-0">
          <ButtonInput label={t("+ New Order")} isSubmit={false} onClick={onNewOrderClick} classes="w-full lg:w-auto whitespace-nowrap" />
        </div>
      </div>
    </div>
  );
}
